import type { CvssMetrics, Cvss4Metrics, CvssVersion } from './cvss-types';
import {
  calculateCvss4Score,
  generateCvss4VectorString,
  parseCvss4VectorString,
  areAllCvss4MetricsSelected,
  getInitialCvss4Metrics,
  calculateCvss4BaseScoreFromVector,
} from './cvss4-utils';
import { calculateAeBaseScore } from './cvss-ae-calculator';

export { getSeverityFromScore } from './cvss-severity';

export const isCvss4Metrics = (
  m: CvssMetrics | Cvss4Metrics,
): m is Cvss4Metrics =>
  typeof (m as Partial<Cvss4Metrics>).attackRequirements === 'string';

const generateCvss3VectorString = (
  metrics: CvssMetrics,
  version: '3.0' | '3.1' = '3.1',
): string =>
  `CVSS:${version}/AV:${metrics.attackVector}/AC:${metrics.attackComplexity}/PR:${metrics.privilegesRequired}/UI:${metrics.userInteraction}/S:${metrics.scope}/C:${metrics.confidentiality}/I:${metrics.integrity}/A:${metrics.availability}`;

const parseCvss3VectorString = (vector: string): Partial<CvssMetrics> => {
  if (!vector.startsWith('CVSS:3.')) {
    return {};
  }

  const parts = vector.split('/');
  const parsedMetrics: Partial<CvssMetrics> = {};

  parts.forEach((part) => {
    const [key, value] = part.split(':');
    switch (key) {
      case 'AV':
        parsedMetrics.attackVector = value;
        break;
      case 'AC':
        parsedMetrics.attackComplexity = value;
        break;
      case 'PR':
        parsedMetrics.privilegesRequired = value;
        break;
      case 'UI':
        parsedMetrics.userInteraction = value;
        break;
      case 'S':
        parsedMetrics.scope = value;
        break;
      case 'C':
        parsedMetrics.confidentiality = value;
        break;
      case 'I':
        parsedMetrics.integrity = value;
        break;
      case 'A':
        parsedMetrics.availability = value;
        break;
    }
  });

  return parsedMetrics;
};

const areAllCvss3MetricsSelected = (metrics: CvssMetrics): boolean =>
  !!(
    metrics.attackVector &&
    metrics.attackComplexity &&
    metrics.privilegesRequired &&
    metrics.userInteraction &&
    metrics.scope &&
    metrics.confidentiality &&
    metrics.integrity &&
    metrics.availability
  );

const getInitialCvss3Metrics = (): CvssMetrics => ({
  attackVector: '',
  attackComplexity: '',
  privilegesRequired: '',
  userInteraction: '',
  scope: '',
  confidentiality: '',
  integrity: '',
  availability: '',
});

const calculateCvss3Score = (
  metrics: CvssMetrics,
  version: '3.0' | '3.1' = '3.1',
): number => {
  if (!areAllCvss3MetricsSelected(metrics)) {
    return 0;
  }
  return calculateAeBaseScore(generateCvss3VectorString(metrics, version)) ?? 0;
};

const calculateCvss3BaseScoreFromVector = (vector: string): number | null =>
  calculateAeBaseScore(vector);

export function calculateCvssScore(
  metrics: CvssMetrics,
  version?: '3.0' | '3.1',
): number;
export function calculateCvssScore(
  metrics: Cvss4Metrics,
  version: '4.0',
): number;
export function calculateCvssScore(
  metrics: CvssMetrics | Cvss4Metrics,
  version?: CvssVersion,
): number;
export function calculateCvssScore(
  metrics: CvssMetrics | Cvss4Metrics,
  version?: CvssVersion,
): number {
  return isCvss4Metrics(metrics)
    ? calculateCvss4Score(metrics)
    : calculateCvss3Score(metrics, version === '3.0' ? '3.0' : '3.1');
}

export function generateVectorString(
  metrics: CvssMetrics,
  version?: '3.0' | '3.1',
): string;
export function generateVectorString(
  metrics: Cvss4Metrics,
  version: '4.0',
): string;
export function generateVectorString(
  metrics: CvssMetrics | Cvss4Metrics,
  version?: CvssVersion,
): string;
export function generateVectorString(
  metrics: CvssMetrics | Cvss4Metrics,
  version?: CvssVersion,
): string {
  if (isCvss4Metrics(metrics)) {
    return generateCvss4VectorString(metrics);
  }
  return generateCvss3VectorString(metrics, version === '3.0' ? '3.0' : '3.1');
}

export function parseVectorString(
  vector: string,
): Partial<CvssMetrics & Cvss4Metrics> {
  if (vector.startsWith('CVSS:4.0')) {
    return parseCvss4VectorString(vector);
  }
  return parseCvss3VectorString(vector);
}

export function areAllMetricsSelected(
  metrics: CvssMetrics,
  version?: '3.0' | '3.1',
): boolean;
export function areAllMetricsSelected(
  metrics: Cvss4Metrics,
  version: '4.0',
): boolean;
export function areAllMetricsSelected(
  metrics: CvssMetrics | Cvss4Metrics,
  version?: CvssVersion,
): boolean;
export function areAllMetricsSelected(
  metrics: CvssMetrics | Cvss4Metrics,
): boolean {
  return isCvss4Metrics(metrics)
    ? areAllCvss4MetricsSelected(metrics)
    : areAllCvss3MetricsSelected(metrics);
}

export function getInitialMetrics(): CvssMetrics;
export function getInitialMetrics(version: '4.0'): Cvss4Metrics;
export function getInitialMetrics(
  version?: Exclude<CvssVersion, '4.0'>,
): CvssMetrics;
export function getInitialMetrics(
  version?: CvssVersion,
): CvssMetrics | Cvss4Metrics {
  return version === '4.0'
    ? getInitialCvss4Metrics()
    : getInitialCvss3Metrics();
}

export function calculateCvssBaseScoreFromVector(
  vector: string,
): number | null {
  if (vector.startsWith('CVSS:4.0')) {
    return calculateCvss4BaseScoreFromVector(vector);
  }
  return calculateCvss3BaseScoreFromVector(vector);
}

export const getCvssVersionFromVector = (
  vector: string,
): CvssVersion | null => {
  const match = /^CVSS:(\d+\.\d+)/.exec(vector);
  if (!match) return null;
  const v = match[1];
  if (v === '3.0' || v === '3.1' || v === '4.0') return v;
  return null;
};

export const buildInformativeImpactMetrics = (version: CvssVersion): string => {
  if (version === '4.0') {
    return 'VC:N/VI:N/VA:N/SC:N/SI:N/SA:N';
  }
  return 'C:N/I:N/A:N';
};
